<template>
  <div>
    <div v-if="!isFetching">
      <v-breadcrumbs class="px-0" :items="breadcrumbs"></v-breadcrumbs>
      <div>
        <v-progress-linear
          v-if="isUpdating"
          indeterminate
          color="primary"
          class="my-8"
        ></v-progress-linear>
      </div>
      <div class="d-flex align-center mb-8">
        <h1 v-if="thisClass">
          <span class="pr-8">
            {{ thisClass.className }}
          </span>
          <span class="pr-8">
            {{ displayDate }}
          </span>
        </h1>
        <div>
          <span v-if="isApproved || isApprovedByChief"
            ><v-chip color="primary" dark>Approved</v-chip></span
          >
          <span v-else-if="applicationStatus === 'applied'"
            ><v-chip color="primary" variant="outlined" dark>Applied</v-chip></span
          >
          <span v-else><v-chip color="grey" variant="outlined" dark>Draft</v-chip></span>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!isApproved"
          variant="flat"
          color="primary"
          large
          class="ml-2"
          :disabled="isUpdating"
          @click="saveAsDraft"
        >
          <div v-if="isDraft">Save as Draft</div>
          <div v-else>Return</div>
        </v-btn>
        <v-btn
          v-if="isDraft"
          variant="flat"
          color="primary"
          large
          class="ml-2"
          :disabled="isUpdating || appliedBy === null"
          @click="saveAndApply"
          >Apply</v-btn
        >
        <div v-if="(isDirector || isChief) && isApplied">
          <v-btn
            variant="flat"
            color="primary"
            large
            class="ml-2"
            :disabled="isUpdating || isApprovedByMe"
            @click="approve"
            >Approve</v-btn
          >
        </div>
        <div
          v-if="
            (isChief && isApplied && isApprovedByChief) ||
            (isDirector && isApplied && isApproved)
          "
        >
          <v-btn
            variant="flat"
            color="error"
            large
            class="ml-2"
            :disabled="isUpdating"
            @click="reject"
            >Revoke Approval</v-btn
          >
        </div>
      </div>

      <v-row>
        <v-col cols="6"> </v-col>
        <v-col cols="2">
          <v-text-field
            label="Director"
            readonly
            variant="outlined"
            :model-value="directorApproveStamp"
          >
          </v-text-field>
        </v-col>
        <v-col cols="2">
          <v-text-field
            label="Chief"
            readonly
            variant="outlined"
            :model-value="cheifApproveStamp"
          >
          </v-text-field>
        </v-col>
        <v-col cols="2">
          <v-select
            variant="outlined"
            label="Applicant"
            :items="staffOptions"
            item-value="userId"
            item-title="name"
            v-model="appliedBy"
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <h3 class="mb-2">Objective</h3>
          <v-textarea
            :readonly="isApproved || isApplied"
            variant="outlined"
            v-model="aim"
          ></v-textarea>
        </v-col>
        <v-col> </v-col>
      </v-row>

      <v-row>
        <v-col>
          <h3 class="mb-2">Activities: Morning</h3>
          <v-textarea
            :readonly="isApproved || isApplied"
            variant="outlined"
            v-model="activityAm"
          ></v-textarea>
        </v-col>
        <v-col>
          <h3 class="mb-2">Activities: Afternoon</h3>
          <v-textarea
            :readonly="isApproved || isApplied"
            variant="outlined"
            v-model="activityPm"
          ></v-textarea>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <h3 class="mb-2">Evaluation & Reflection</h3>
          <v-textarea
            :readonly="isApproved || isApplied"
            variant="outlined"
            v-model="assessment"
          ></v-textarea>
        </v-col>
        <v-col>
          <h3 class="mb-2">Parent Support</h3>
          <v-textarea
            :readonly="isApproved || isApplied"
            variant="outlined"
            v-model="support"
          ></v-textarea>
        </v-col>
      </v-row>

      <div class="pt-8">
        <h3 class="my-2">
          Class Children Records
          {{ childReportList ? childReportList.length + " children" : "" }}
        </h3>
        <v-card
          v-for="(item, i) in childReportList"
          :key="i"
          class="px-6 py-6 my-3"
          flat
          variant="outlined"
        >
          <v-container>
            <v-row>
              <v-col cols="12" class="py-0">
                <span
                  v-if="item.attendance && item.attendance.status == 'isAbsent'"
                >
                  <v-chip color="red" variant="outlined">Absent</v-chip>
                </span>
              </v-col>
              <v-col cols="12">
                <v-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th
                          v-for="header in tableHeader"
                          :key="header.value"
                          style="width: 14%"
                        >
                          {{ header.text }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <span class="mr-2">{{
                            item.child.lastName + item.child.firstName
                          }}</span>
                        </td>
                        <td>
                          <div
                            v-for="sleep in item.report.sleeps"
                            :key="sleep.sleepId"
                          >
                            {{ sleep ? formatDate(sleep.from) : "" }} ~
                            {{ sleep ? formatDate(sleep.to) : "" }}
                          </div>
                        </td>
                        <td>
                          <div
                            v-for="temperature in item.report.temperatures"
                            :key="temperature.temperatureId"
                          >
                            <span class="grey--text mr-2">
                              {{
                                temperature
                                  ? formatDate(temperature.recordedAt)
                                  : ""
                              }}
                            </span>
                            <span class="">
                              {{
                                temperature
                                  ? formatTemperature(temperature.temperature)
                                  : ""
                              }}℃
                            </span>
                          </div>
                        </td>
                        <td>
                          <div
                            v-for="defecation in item.report.defecations"
                            :key="defecation.defecationId"
                          >
                            <span class="grey--text mr-2">
                              {{
                                defecation
                                  ? formatDate(defecation.recordedAt)
                                  : ""
                              }}
                            </span>
                            <span class="">
                              {{
                                defecation
                                  ? formatDefecation(defecation.state)
                                  : ""
                              }}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div
                            v-for="milk in item.report.milks"
                            :key="milk.milkId"
                          >
                            <span class="grey--text mr-2">
                              {{ milk ? formatDate(milk.recordedAt) : "" }}
                            </span>
                            <span class="">
                              {{
                                milk.amount ? milk.amount.value : "(Unknown)"
                              }}cc
                            </span>
                          </div>
                        </td>
                        <td v-if="item.meal">
                          <div>
                            <span class="grey--text mr-2">Main Dish</span>
                            <span>{{
                              formatMealStatus(item.meal.stapleFood)
                            }}</span>
                          </div>
                          <div>
                            <span class="grey--text mr-2">Side Dish 1</span>
                            <span>{{
                              formatMealStatus(item.meal.mainDish)
                            }}</span>
                          </div>
                          <div>
                            <span class="grey--text mr-2">Side Dish 2</span>
                            <span>{{
                              formatMealStatus(item.meal.sideDish)
                            }}</span>
                          </div>
                          <div>
                            <span class="grey--text mr-2">Soup</span>
                            <span>{{ formatMealStatus(item.meal.soup) }}</span>
                          </div>
                        </td>
                        <td v-if="item.meal">
                          <div>
                            <span class="grey--text mr-2">Dessert</span>
                            <span>{{
                              formatMealStatus(item.meal.dessert)
                            }}</span>
                          </div>
                          <div>
                            <span class="grey--text mr-2">Cow Milk</span>
                            <span>{{ formatMealStatus(item.meal.milk) }}</span>
                          </div>
                          <div>
                            <span class="grey--text mr-2">Snack</span>
                            <span>{{ formatMealStatus(item.meal.snack) }}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-col>
            </v-row>

            <v-row class="pt-3 px-3">
              <!-- <v-text-field -->
                <v-textarea
                  :model-value="item.diary?.content" auto-grow
                  variant="filled" readonly
                  label="Activity Record & Development"
                  hide-details
                ></v-textarea>
            </v-row>
          </v-container>
        </v-card>
      </div>
    </div>

    <div v-else>
      <v-progress-linear indeterminate color="primary"></v-progress-linear>
    </div>
  </div>
</template>
<script lang="ts">
import {
  DiaryResponse,
  MealResponse,
  NurseryChildResponse,
  NurseryClassResponse,
  NurseryDailyReportResponse,
  SearchNurseryAttendanceResponse,
  NurseryDiarySettingResponse,
} from "chaild-api/lib";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import { classDiaryModle } from "@/store/dataModules/classDiaryModue";
import { nurseryUserModule } from "@/store/dataModules/nurseryUserModule";
import { defineComponent } from "vue";
import { meModule } from "@/store/dataModules/meModule";
import ApiChildren from "@/api/ApiChildren";
import ApiClassDiary from "@/api/ApiClassDiary";
import ApiAttendance from "@/api/ApiAttendance";
import dayjs from "dayjs";
import ApiChildReport from "@/api/ApiChildDiary";
import { diarySettingModule } from "@/store/dataModules/diarySettingModule";

interface DataType {
  classDiaryId: number | null;
  classId: number | null;
  isFetching: boolean;
  isUpdating: boolean;
  childReportList: ChildReportItem[];
  alertSetting: {
    shouldAlert: boolean;
    children: string[];
  };
}

interface BreadcrumbItem {
  title: string;
  disabled: boolean;
  href: string;
  color: string;
}

interface ChildReportItem {
  child?: NurseryChildResponse;
  report?: NurseryDailyReportResponse;
  attendance?: SearchNurseryAttendanceResponse;
  meal?: MealResponse;
  diary?: DiaryResponse;
}

export default defineComponent({
  data: (): DataType => {
    return {
      classDiaryId: null,
      classId: null,
      isFetching: false,
      isUpdating: false,
      childReportList: [] as ChildReportItem[],
      alertSetting: {
        shouldAlert: false,
        children: [],
      },
    };
  },

  computed: {
    thisClass(): NurseryClassResponse | null {
      if (!this.diary) {
        return null;
      }
      const classes = classModule.classes;
      const find = classes.find((c) => c.classId === this.diary?.classId);
      if (find) {
        return find as NurseryClassResponse;
      }
      return null;
    },
    breadcrumbs(): Array<BreadcrumbItem> {
      return [
        {
          title: "Class Diary List",
          disabled: false,
          href: "/class-diaries",
          color: "primary"
        },
        {
          title: this.thisClass ? this.thisClass.className : "",
          disabled: true,
          href: "",
          color: "grey"
        },
      ];
    },
    diary() {
      return classDiaryModle.diary;
    },
    staffOptions() {
      return nurseryUserModule.staffs.filter(s => !s.isHidden).map((staff) => {
        return {
          userId: staff.userId,
          name: `${staff.lastName} ${staff.firstName}`,
        };
      });
    },
    me() {
      return meModule.me;
    },
    isDirector() {
      if (this.me) {
        const nurseryId = LocalDataService.getNurseryId();
        const currentAccount = this.me.nurserySchools.find(
          (s) => s.nurseryId === nurseryId
        );
        if (currentAccount && currentAccount.isDirector) {
          return true;
        }
      }
      return false;
    },
    isChief() {
      if (this.me) {
        const nurseryId = LocalDataService.getNurseryId();
        const currentAccount = this.me.nurserySchools.find(
          (s) => s.nurseryId === nurseryId
        );
        if (currentAccount && currentAccount.isChief) {
          return true;
        }
      }
      return false;
    },
    isAuthor(): boolean {
      if (this.me && this.diary) {
        const nurseryId = LocalDataService.getNurseryId();
        const currentAccount = this.me.nurserySchools.find(
          (s) => s.nurseryId === nurseryId
        );
        if (
          currentAccount &&
          currentAccount.userId === this.diary.createdBy.userNurseryId
        ) {
          return true;
        }
      }
      return false;
    },
    cheifApproveStamp(): string {
      if (this.diary) {
        if (this.diary.approvedBy.chief && this.diary.approvedBy.chief != -1) {
          return `${this.diary.approvedBy.chief.lastName} ${this.diary.approvedBy.chief.firstName}`;
        }
      }
      return "-";
    },
    directorApproveStamp(): string {
      if (this.diary) {
        if (
          this.diary.approvedBy.director &&
          this.diary.approvedBy.director != -1
        ) {
          return `${this.diary.approvedBy.director.lastName} ${this.diary.approvedBy.director.firstName}`;
        }
      }
      return "-";
    },
    recorderStamp(): string {
      if (this.diary) {
        if (this.diary.createdBy) {
          return `${this.diary.createdBy.lastName} ${this.diary.createdBy.firstName}`;
        }
      }
      return "Draft";
    },
    date(): string {
      if (this.diary) {
        return this.diary.date;
      }
      return "";
    },
    displayDate(): string {
      return this.date ? dayjs(this.date).format("DD/MM/YYYY") : "";
    },
    applicationStatus(): string {
      if (this.diary) {
        return this.diary.status;
      }
      return "";
    },
    isApproved(): boolean {
      if (this.diary) {
        return (
          this.diary.approvedBy.director != null &&
          this.diary.approvedBy.director != -1
        );
      }
      return false;
    },
    isApprovedByChief(): boolean {
      if (this.diary) {
        return (
          this.diary.approvedBy.chief != null &&
          this.diary.approvedBy.chief != -1
        );
      }
      return false;
    },
    isApprovedByMe(): boolean {
      if (this.diary && meModule.me) {
        if (
          this.diary.approvedBy &&
          this.diary.approvedBy.director &&
          this.diary.approvedBy.director != -1
        ) {
          const directorNuseryId = this.diary.approvedBy.director.nurseryId;
          const directorUserId = this.diary.approvedBy.director.userNurseryId;
          const findAccount = meModule.me.nurserySchools.find(
            (ac) => ac.nurseryId === directorNuseryId
          );
          if (findAccount && findAccount.userId === directorUserId) {
            return true;
          }
        }

        if (
          this.diary.approvedBy &&
          this.diary.approvedBy.chief &&
          this.diary.approvedBy.chief != -1
        ) {
          const chiefNuseryId = this.diary.approvedBy.chief.nurseryId;
          const chiefUserId = this.diary.approvedBy.chief.userNurseryId;
          const findAccount = meModule.me.nurserySchools.find(
            (ac) => ac.nurseryId === chiefNuseryId
          );
          if (findAccount && findAccount.userId === chiefUserId) {
            return true;
          }
        }

        return false;
      }
      return false;
    },
    isDraft(): boolean {
      if (this.diary) {
        return this.diary.status === "draft";
      }
      return false;
    },
    isApplied(): boolean {
      if (this.diary) {
        return this.diary.status === "applied";
      }
      return false;
    },
    appliedBy: {
      get() {
        return classDiaryModle.appliedBy;
      },
      set(value: number | null) {
        classDiaryModle.setAppliedBy(value);
      },
    },
    aim: {
      get(): string | null {
        if (this.diary) {
          return this.diary.aim;
        }
        return null;
      },
      set(value: string) {
        if (this.diary) {
          const diary = { ...this.diary };
          diary.aim = value;
          classDiaryModle.updateDiaryValue(diary);
        }
      },
    },
    activityAm: {
      get(): string | null {
        if (this.diary) {
          return this.diary.activityAm;
        }
        return null;
      },
      set(value: string) {
        if (this.diary) {
          const diary = { ...this.diary };
          diary.activityAm = value;
          classDiaryModle.updateDiaryValue(diary);
        }
      },
    },
    activityPm: {
      get(): string | null {
        if (this.diary) {
          return this.diary.activityPm;
        }
        return null;
      },
      set(value: string) {
        if (this.diary) {
          const diary = { ...this.diary };
          diary.activityPm = value;
          classDiaryModle.updateDiaryValue(diary);
        }
      },
    },
    assessment: {
      get(): string | null {
        if (this.diary) {
          return this.diary.assessment;
        }
        return null;
      },
      set(value: string) {
        if (this.diary) {
          const diary = { ...this.diary };
          diary.assessment = value;
          classDiaryModle.updateDiaryValue(diary);
        }
      },
    },
    support: {
      get(): string | null {
        if (this.diary) {
          return this.diary.support;
        }
        return null;
      },
      set(value: string) {
        if (this.diary) {
          const diary = { ...this.diary };
          diary.support = value;
          classDiaryModle.updateDiaryValue(diary);
        }
      },
    },
    tableHeader() {
      return [
        { text: "Child Name", value: "name" },
        { text: "Sleep", value: "sleep" },
        { text: "Temperature", value: "temperature" },
        { text: "Defecation", value: "defecation" },
        { text: "Milk", value: "milk" },
        { text: "Meals", value: "meal" },
        { text: "", value: "meal2" },
      ];
    },
    diarySetting: function (): NurseryDiarySettingResponse | null {
      return diarySettingModule.diarySetting;
    },
  },

  methods: {
    async getDiary(classId: number, classDiaryId: number) {
      try {
        await classDiaryModle.findDiary({ classId, classDiaryId });
      } catch {
        this.isFetching = false;
      }
    },
    async saveAsDraft() {
      this.isUpdating = true;
      await classDiaryModle.saveAsDraft();
      this.isUpdating = false;
    },
    async saveAndApply() {
      this.isUpdating = true;
      await classDiaryModle.saveAndApply();
      this.isUpdating = false;
    },
    async approve() {
      this.isUpdating = true;
      if (this.alertSetting.shouldAlert) {
        const answer = confirm(
          `There are children with unfilled "Activity Record & Development". Do you want to approve?\n\n${this.alertSetting.children.join(
            "\n"
          )}`
        );
        if (answer) {
          await classDiaryModle.approve();
        }
      } else {
        await classDiaryModle.approve();
      }
      this.isUpdating = false;
    },
    async reject() {
      this.isUpdating = true;
      await classDiaryModle.reject();
      this.isUpdating = false;
    },
    async listChildrenReport(input: { classId: number }) {
      const nurseryId = LocalDataService.getNurseryId();

      if (input.classId && nurseryId && this.date) {
        // list children
        const childrenResponse = await ApiChildren.listChildren({
          classId: input.classId,
          nurseryId: nurseryId,
          limit: 1000,
        });

        // list report
        const reportResponse = await ApiClassDiary.listChildrenReport({
          classId: input.classId,
          date: this.date,
        });

        // list attendance
        const attendanceResponse = await ApiAttendance.searchAttendance({
          nurseryId: nurseryId,
          classId: input.classId,
          date: this.date,
          limit: 1000,
        });

        // list meal
        const mealResponse = await ApiClassDiary.listChildrenMeal({
          classId: input.classId,
          date: this.date,
        });

        const diaryResponse = await ApiChildReport.searchChildDiary({
          nurseryId: nurseryId,
          classId: input.classId,
          date: this.date,
          limit: 1000,
        });

        childrenResponse.forEach((child) => {
          const childId = child.childId;
          const childReportItem: ChildReportItem = {};
          const theDay = dayjs(this.date);

          // 卒園・転園の場合
          if (child.quitDate) {
            if (theDay && theDay.isAfter(dayjs(child.quitDate))) {
              return;
            }
          }

          // 入園前のものは非表示
          if (child.admissionDate) {
            if (theDay && theDay.isBefore(dayjs(child.admissionDate))) {
              return;
            }
          }

          childReportItem.child = child;
          childReportItem.report = reportResponse.reports.find(
            (r) => r.childId == childId
          );
          childReportItem.attendance = attendanceResponse.attendances.find(
            (a) => a.child.childId == childId
          );
          childReportItem.meal = mealResponse.diaries.find(
            (m) => m.childId == childId
          );
          childReportItem.diary = diaryResponse.diaries.find(
            (d) => d.child.childId == childId
          );

          if (childReportItem.report == undefined) {
            childReportItem.report = {
              childId: childId,
              defecations: [],
              milks: [],
              sleeps: [],
              temperatures: [],
            };
          }

          this.childReportList.push(childReportItem);

          // alert setting
          const classId = Number(this.$route.params.classId);
          if (
            this.diarySetting?.classDiaryConfirmation.targetClasses.some(
              (t) => t.classId == classId
            )
          ) {
            if (!childReportItem.diary || childReportItem.diary.content == "") {
              this.alertSetting.shouldAlert = true;
              this.alertSetting.children.push(
                `${child.lastName}${child.firstName}`
              );
            }
          }
        });
      }
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("HH:mm");
    },
    formatTemperature(value: number): string {
      return value.toFixed(1);
    },
    formatDefecation(defecation: string) {
      if (defecation === "hard") {
        return "Hard";
      } else if (defecation === "normal") {
        return "Normal";
      } else if (defecation === "soft") {
        return "Soft";
      } else if (defecation === 'diarrhea') {
        return 'Watery (Diarrhea)';
      }
    },
    formatMealStatus(status: string) {
      if (status === "finished") {
        return "Ate All";
      } else if (status === "refill") {
        return "Seconds";
      } else if (status === "unfinished") {
        return "Left a Little";
      } else if (status === "half") {
        return "Ate Half";
      } else if (status === "little") {
        return "Ate a Little";
      } else if (status === "notEat") {
        return "Did Not Eat";
      } else if (status === "notServed") {
        return "Not Served";
      }
    },
  },

  async mounted() {
    this.isFetching = true;

    const classId = this.$route.params.classId as string;
    const classDiaryId = this.$route.params.classDiaryId as string;

    if (classId && classDiaryId) {
      try {
        const classIdInt = parseInt(classId);
        const classDiaryIdInt = parseInt(classDiaryId);

        await this.getDiary(classIdInt, classDiaryIdInt);

        await this.listChildrenReport({
          classId: classIdInt,
        });
      } catch {
        return;
      }
    }

    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      await classModule.listClass({
        nurseryId: nurseryId,
      });
      await nurseryUserModule.listStaffs({
        date: this.date,
        diaryId: Number(classDiaryId),
        diaryType: 'class'
      });
      await diarySettingModule.listDiarySetting({ nurseryId });
    }

    this.isFetching = false;
  },
});
</script>
